import { add, qs, remove } from '@fluorescent/dom';
import Delegate from 'ftdomdelegate';
import { Ajaxinate } from 'ajaxinate';

import { section } from '@/glow';
import { emit, hydrate } from '@/glow/events';

import {
  filtersUpdated,
  removeFilters,
  removeRange,
  clearAll,
  filtersRemoved,
  rangeRemoved,
  everythingCleared,
} from '@/lib/collection/events';

const selectors = {
  infiniteScrollContainer: '.collection__infinite-container',
  infiniteScrollTrigger: '.collection__infinite-trigger',
  partial: '[data-partial]',
};

section('collection', {
  infiniteScroll: null,

  onLoad() {
    const { collectionItemCount, paginationType } = this.container.dataset;

    if (!parseInt(collectionItemCount)) return;

    this.filterForm = qs('[data-filter-form]', this.container);

    if (this.filterForm) {
      this._initFiltering();
    }

    // Ininite scroll
    this.paginationType = paginationType;
    this.paginated = this.paginationType === 'paginated';

    this.infiniteScrollTrigger = qs(
      selectors.infiniteScrollTrigger,
      this.container
    );

    if (!this.paginated) {
      this._initInfiniteScroll();
    }
  },

  _initFiltering() {
    import('@/lib/collection/filtering').then(({ default: filtering }) => {

      // collection filters
      this.filtering = filtering(this.filterForm, this.container);

      // Set initial evx state from collection url object
      hydrate(this.filtering.getState());

      this.partial = qs(selectors.partial, this.container);

      this.subscriptions = [
        filtersRemoved((_, { target }) => {
          this.filtering.removeFilters(target, data => {
            this._renderView(data.url);
            hydrate(data)();
          });
        }),

        rangeRemoved(() => {
          this.filtering.removeRange(data => {
            this._renderView(data.url);
            hydrate(data)();
          });
        }),

        filtersUpdated(() => {
          this.filtering.filtersUpdated(data => {
            this._renderView(data.url);
            hydrate(data)();
          });
        }),

        everythingCleared(() => {
          this.filtering.clearAll(data => {
            this._renderView(data.url);
            hydrate(data)();
          });
        }),
      ];

      this.delegate = new Delegate(this.partial);

      this.delegate.on('click', '[data-remove-filter]', e => {
        e.preventDefault();
        const { filter } = e.target.dataset;
        removeFilters(filter);
      });

      this.delegate.on('click', '[data-remove-range]', e => {
        e.preventDefault();
        removeRange();
      });

      this.delegate.on('click', '[data-clear]', e => {
        e.preventDefault();
        clearAll();
      });

    });
  },

  _initInfiniteScroll() {
    const infiniteScrollOptions = {
      container: selectors.infiniteScrollContainer,
      pagination: selectors.infiniteScrollTrigger,
      loadingText: 'Loading...',
      callback: () => emit('collection:updated'),
    };

    if (this.paginationType === 'click') {
      infiniteScrollOptions.method = 'click';
    }

    this.infiniteScroll = new Ajaxinate(infiniteScrollOptions);
  },

  _renderView(searchParams) {
    const url = `${window.location.pathname}?section_id=${this.container.dataset.sectionId}&${searchParams}`;
    const loading = qs('.collection__loading', this.container);

    add(loading, 'is-active');

    fetch(url)
      .then(res => res.text())
      .then(res => {
        this._updateURLHash(searchParams);
        const doc = new DOMParser().parseFromString(res, 'text/html');
        const contents = qs(selectors.partial, doc).innerHTML;
        this.partial.innerHTML = contents;

        if (!this.paginated) {
          this.infiniteScrollTrigger.innerHTML = '';

          this._initInfiniteScroll();
        }

        remove(loading, 'is-active');
        emit('collection:updated');
      });
  },

  _updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  },

  onUnload() {
    this.infiniteScroll && this.infiniteScroll.destroy();

    this.filtering && this.filtering.unload();

    this.delegate.off();
    this.subscriptions.forEach(unsubscribe => unsubscribe());
  },
});
